import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { getTatlStory, listTatlStories, normalizeImageUrl } from "../lib/tatlApi";
import { formatDate } from "../lib/utils";

const SectionCard = ({ title, children }) => (
  <Card className="rounded-2xl border-stone-200 shadow-sm">
    <CardContent className="p-6">
      <h3 className="font-heading text-xl font-bold text-stone-900 mb-4">{title}</h3>
      {children}
    </CardContent>
  </Card>
);

const Bullets = ({ items }) => {
  const list = Array.isArray(items) ? items.filter(Boolean) : [];
  if (list.length === 0) return <p className="text-stone-600">No update provided yet.</p>;
  return (
    <ul className="space-y-2">
      {list.map((x, i) => (
        <li key={i} className="flex gap-3">
          <span className="mt-2 w-2 h-2 rounded-full bg-amber-600 shrink-0" />
          <span className="text-stone-700">{x}</span>
        </li>
      ))}
    </ul>
  );
};

const YouTubeEmbed = ({ url }) => {
  const src = useMemo(() => {
    if (!url) return null;
    const u = String(url).trim();
    if (!u) return null;

    // Accept youtube.com/watch?v= or youtu.be/
    const m1 = u.match(/[?&]v=([^&]+)/i);
    const m2 = u.match(/youtu\.be\/([^?&]+)/i);
    const id = (m1 && m1[1]) || (m2 && m2[1]) || null;
    if (!id) return null;
    return `https://www.youtube.com/embed/${id}`;
  }, [url]);

  if (!src) return null;

  return (
    <div className="aspect-video w-full overflow-hidden rounded-2xl border border-stone-200">
      <iframe
        className="w-full h-full"
        src={src}
        title="TATL Video"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
};

const TatlStoryDetails = () => {
  const { slug } = useParams();
  const [story, setStory] = useState(null);
  const [latest, setLatest] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const s = await getTatlStory(slug);
      setStory(s);

      const all = await listTatlStories(50);
      setLatest(Array.isArray(all) ? all.filter((x) => x.id !== s.id).slice(0, 8) : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-14">
        <div className="h-72 rounded-3xl bg-stone-200 animate-pulse mb-8" />
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-28 rounded-2xl bg-stone-200 animate-pulse" />
            ))}
          </div>
          <div className="lg:col-span-4 space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 rounded-2xl bg-stone-200 animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!story) {
    return (
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-16">
        <Card className="rounded-2xl border-stone-200">
          <CardContent className="p-6">
            <p className="text-stone-700">Story not found.</p>
            <Button asChild className="mt-4 rounded-xl">
              <Link to="/tatl1000">Back to Updates</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Hero header */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={normalizeImageUrl(story.cover_image_url)}
            alt={story.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/75 via-black/40 to-blue-950/60" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <Link to="/tatl1000" className="text-white/85 hover:text-white text-sm">
            ← Back to TATL1000 Updates
          </Link>

          <div className="mt-5 flex flex-wrap gap-2">
            <span className="inline-flex rounded-full bg-white/15 border border-white/20 px-4 py-2 text-xs font-semibold text-white">
              {formatDate(story.impact_date)}
            </span>
            <span className="inline-flex rounded-full bg-amber-600/90 px-4 py-2 text-xs font-semibold text-white">
              #TATL1000
            </span>
            <span className="inline-flex rounded-full bg-white/15 border border-white/20 px-4 py-2 text-xs font-semibold text-white">
              {story.location}
            </span>
          </div>

          <h1 className="mt-5 font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight">
            {story.title}
          </h1>

          <p className="mt-4 text-white/90 max-w-3xl">
            <span className="font-semibold">{story.child_name}</span>
            {story.age ? ` • ${story.age} years old` : ""}
            {story.gender ? ` • ${story.gender}` : ""}
          </p>

          <p className="mt-5 text-white/85 max-w-3xl text-base sm:text-lg">
            {story.excerpt}
          </p>

          <div className="mt-7 flex flex-wrap gap-3">
            <Button asChild className="bg-amber-600 hover:bg-amber-700 rounded-xl">
              <Link to="/donate-now">Support More Lives</Link>
            </Button>
            <Button asChild variant="outline" className="rounded-xl border-white text-white hover:bg-white/10">
              <Link to="/get-involved#volunteer">Volunteer</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Main content */}
          <div className="lg:col-span-8 space-y-6">
            {story.quote ? (
              <Card className="rounded-2xl border-stone-200 bg-amber-50">
                <CardContent className="p-6">
                  <p className="text-stone-900 font-semibold text-lg leading-relaxed">
                    “{story.quote}”
                  </p>
                  <p className="mt-3 text-stone-600 text-sm">
                    — TATL1000 Update
                  </p>
                </CardContent>
              </Card>
            ) : null}

            <SectionCard title="Story Board">
              <div
                className="prose max-w-none prose-stone"
                dangerouslySetInnerHTML={{ __html: story.content || "" }}
              />
            </SectionCard>

            <div className="grid md:grid-cols-2 gap-6">
              <SectionCard title="Highlights">
                <Bullets items={story.highlights} />
              </SectionCard>

              <SectionCard title="Needs Identified">
                <Bullets items={story.needs} />
              </SectionCard>

              <SectionCard title="Support Given">
                <Bullets items={story.support_given} />
              </SectionCard>

              <SectionCard title="Outcomes">
                <Bullets items={story.outcomes} />
              </SectionCard>
            </div>

            {Array.isArray(story.gallery) && story.gallery.length > 0 ? (
              <SectionCard title="Gallery">
                <div className="grid sm:grid-cols-2 gap-4">
                  {story.gallery.map((g, i) => (
                    <div key={i} className="rounded-2xl overflow-hidden border border-stone-200">
                      <img
                        src={normalizeImageUrl(g)}
                        alt={`Gallery ${i + 1}`}
                        className="w-full h-60 object-cover"
                      />
                    </div>
                  ))}
                </div>
              </SectionCard>
            ) : null}

            {story.video_url ? (
              <SectionCard title="Video Update">
                <YouTubeEmbed url={story.video_url} />
              </SectionCard>
            ) : null}

            <Card className="rounded-2xl border-stone-200">
              <CardContent className="p-6">
                <h3 className="font-heading text-xl font-bold text-stone-900">
                  Want to support more stories like this?
                </h3>
                <p className="text-stone-600 mt-2">
                  Join the movement. Donate, volunteer, or partner with us to touch more lives.
                </p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Button asChild className="bg-blue-950 hover:bg-blue-900 rounded-xl">
                    <Link to="/donate-now">Donate Now</Link>
                  </Button>
                  <Button asChild variant="outline" className="rounded-xl">
                    <Link to="/get-involved#partner">Become a Partner</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4 space-y-6">
            <Card className="rounded-2xl border-stone-200 shadow-sm">
              <CardContent className="p-6">
                <h3 className="font-semibold text-stone-900">Quick Profile</h3>
                <div className="mt-4 space-y-3 text-sm">
                  <div className="flex justify-between gap-3">
                    <span className="text-stone-500">Name</span>
                    <span className="font-semibold text-stone-900">{story.child_name}</span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-stone-500">Location</span>
                    <span className="font-semibold text-stone-900">{story.location}</span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-stone-500">Impact Date</span>
                    <span className="font-semibold text-stone-900">{formatDate(story.impact_date)}</span>
                  </div>
                  {story.age ? (
                    <div className="flex justify-between gap-3">
                      <span className="text-stone-500">Age</span>
                      <span className="font-semibold text-stone-900">{story.age}</span>
                    </div>
                  ) : null}
                  {story.gender ? (
                    <div className="flex justify-between gap-3">
                      <span className="text-stone-500">Gender</span>
                      <span className="font-semibold text-stone-900">{story.gender}</span>
                    </div>
                  ) : null}
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-stone-200 shadow-sm">
              <CardContent className="p-6">
                <h3 className="font-semibold text-stone-900 mb-3">Latest TATL1000 Updates</h3>
                <div className="space-y-3">
                  {(latest || []).map((s) => (
                    <Link
                      key={s.id}
                      to={`/tatl1000/${s.slug || s.id}`}
                      className="block p-3 rounded-xl border border-stone-200 hover:border-amber-300 hover:bg-amber-50 transition"
                    >
                      <p className="text-sm font-semibold text-stone-900 line-clamp-1">
                        {s.child_name}: {s.title}
                      </p>
                      <p className="text-xs text-stone-500">
                        {formatDate(s.impact_date)} • {s.location}
                      </p>
                    </Link>
                  ))}
                </div>

                <Button asChild variant="outline" className="mt-4 rounded-xl w-full">
                  <Link to="/tatl1000">View All Updates</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TatlStoryDetails;
